import cx from 'classnames';
import {forwardRef, useCallback, useEffect, useRef} from 'react';
import {useStore} from 'react-redux';
import {createPortal} from 'react-dom';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {Button} from '~library/atoms/Button';
import {UPDATE_GIFT_OPTIONS} from '~/common/fabric/reducers/giftingOptions/constants';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {BottomSheet} from '~library/molecules/BottomSheet';
import {Typography} from '~library/atoms/Typography';
import {DiamondIcon} from '~library/svg/DiamondIcon';
import {sendUpsellTrack, trackUpsell} from '~/common/utils/upsellTracking';
import {EVENT_STATUS_DRAFT} from '~/common/utils/event_constants';
import {SET_UPSELL_CONFIRMED} from '~/CreateFabricInvitation/reducers/modals';
import {UPDATE_EVENT_DETAILS} from '~/CreateFabricInvitation/reducers/details/constants';

import './UpsellModal.sass';

export const UPSELL_MODES = Object.freeze({
  REMOVE_ADS: 'remove-ads',
  UNLOCK_DESIGN_TOOLS: 'unlock-design-tools',
  GIFTING: 'gifting',
  SHAREABLE_LINK: 'shareable-link',
  CUSTOM_SHAREABLE_LINK: 'custom-shareable-link',
  CURATED_SET: 'curated-set',
  PHOTO_SHARING: 'photo-sharing',
});

const HEADER_TEXT = {
  [UPSELL_MODES.REMOVE_ADS]: {
    title: 'Upgrade to remove ads!',
  },
  [UPSELL_MODES.UNLOCK_DESIGN_TOOLS]: {
    title: 'Make your invitation ✨pop✨',
  },
  [UPSELL_MODES.GIFTING]: {
    title: 'Upgrade to remove gift ideas',
  },
  [UPSELL_MODES.SHAREABLE_LINK]: {
    title: 'Create a custom URL',
  },
  [UPSELL_MODES.CUSTOM_SHAREABLE_LINK]: {
    title: 'Customize your shareable link',
  },
  [UPSELL_MODES.CURATED_SET]: {
    title: 'Save time with this curated set!',
  },
  [UPSELL_MODES.PHOTO_SHARING]: {
    title: 'Unlock photo sharing with Premium',
    subtitle: 'Allow the host and guests to share photos after the event.',
  },
};

const GET_WITH_PREMIUM_POINTS = {
  [UPSELL_MODES.REMOVE_ADS]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
  ],
  [UPSELL_MODES.UNLOCK_DESIGN_TOOLS]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
    'No ads!',
  ],
  [UPSELL_MODES.GIFTING]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
    'No ads!',
  ],
  [UPSELL_MODES.SHAREABLE_LINK]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'No ads!',
  ],
  [UPSELL_MODES.CUSTOM_SHAREABLE_LINK]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'No ads!',
  ],
  [UPSELL_MODES.CURATED_SET]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
    'No ads!',
  ],
  [UPSELL_MODES.PHOTO_SHARING]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
    'No ads!',
  ],
};

const isInvitationMaker = window.location.pathname.startsWith('/invitation-maker');

export const UpsellModalContent = forwardRef(
  (
    {
      containerDataQaId,
      toggleUpsellModal,
      closeOverlay,
      noAutoRestore,
      onConfirmAction,
      cancelDataQaId,
      okDataQaId,
      closeDataQaId,
      upgradeToPremium,
      mode = 'gifting',
      hideCloseButton = false,
      hideSubtitle = false,
      className,
      cancelCtaText,
      useRedux = true,
      okCtaText,
      headerVariant = 'header2',
      inlineHeader = false,
      onDenyAction,
    },
    ref
  ) => {
    let store = null;
    if (useRedux) {
      store = useStore();
    }
    const isMediumAndUp = useIsMediumAndUp();
    const modalMode = mode in HEADER_TEXT ? mode : 'gifting';

    const onUpgradeToPremium = useCallback(() => {
      const event = store?.getState()?.eviteEvent || window.evite_event;
      trackUpsell({
        path: isInvitationMaker
          ? '/invitation-maker'
          : window.location.pathname.replace(/\/$/, '').split('/').at(-1),
        feature: mode,
        status: event?.status || EVENT_STATUS_DRAFT,
      });
      if (event?.id) {
        sendUpsellTrack(event?.id);
      }
      if (useRedux) {
        store.dispatch({
          type: SET_UPSELL_CONFIRMED,
          payload: true,
        });
        store.dispatch({
          type: UPDATE_EVENT_DETAILS,
          payload: {isPremium: true},
        });
      }
      upgradeToPremium?.();
    }, [mode, store, useRedux, upgradeToPremium]);

    return (
      <div
        className={cx('create-flow__upsell-modal-content', 'variant-b', className)}
        ref={ref}
        data-qa-id={containerDataQaId}
      >
        {hideCloseButton ? null : (
          <button
            className="create-flow__upsell-modal-close-btn variant-b"
            data-qa-id={closeDataQaId || 'upsell-close'}
            onClick={toggleUpsellModal}
            type="button"
            aria-label="Close modal"
          >
            <CloseLineIcon ratio={0.75} />
          </button>
        )}
        <div className="create-flow__upsell-modal-b__content">
          <div className={cx('create-flow__upsell-modal-b__header', {inline: inlineHeader})}>
            <div>
              <img
                src="https://g0.evitecdn.com/marketing/assets/PremiumDiamondAni.gif"
                alt=""
                width={142}
                height={108}
                className="create-flow__upsell-modal-b__diamond-ani"
              />
              <Typography variant={headerVariant} color="roseWebText" inline>
                Premium
              </Typography>
            </div>
            <Typography variant={headerVariant}>{HEADER_TEXT[modalMode].title}</Typography>
            {HEADER_TEXT[modalMode].subtitle && !hideSubtitle ? (
              <Typography variant="paragraph2">{HEADER_TEXT[modalMode].subtitle}</Typography>
            ) : null}
          </div>
          <div className="create-flow_upsell-modal-b__feature-list">
            {modalMode !== 'unlock-design-tools' && (
              <Typography variant="list2">Also includes:</Typography>
            )}
            {GET_WITH_PREMIUM_POINTS[modalMode].map((point, index) => (
              <div className="create-flow__upsell-modal-b__feature" key={`feature_${index + 1}`}>
                <DiamondIcon />
                <Typography variant="paragraph2">{point}</Typography>
              </div>
            ))}
            <Typography variant="list1">*Premium invitations start at $17.99</Typography>
          </div>
        </div>
        <div className={cx({'create-flow__upsell-modal-b__ctas': true})}>
          <Button
            variant="primary"
            data-qa-id={okDataQaId || 'upsell-upgrade-now'}
            onClick={() => {
              onUpgradeToPremium();
              onConfirmAction();
            }}
            size="large"
            noTextWrap
            fullWidth
          >
            {okCtaText || 'Switch to Premium'}
          </Button>
          <Button
            variant={isMediumAndUp ? 'secondary' : 'unstyled-button'}
            data-qa-id={cancelDataQaId || 'upsell-back'}
            className="upsell-stay-in-basic"
            onClick={() => {
              if (onDenyAction) {
                onDenyAction();
                return closeOverlay();
              }

              if (!noAutoRestore && store) {
                store.dispatch({
                  type: UPDATE_GIFT_OPTIONS,
                  payload: {
                    options: {event_option_show_gifting: true},
                    showGifting: true,
                  },
                });
              }
              return closeOverlay();
            }}
            size="large"
            noTextWrap
            fullWidth
          >
            {cancelCtaText || 'Stay in basic'}
          </Button>
        </div>
      </div>
    );
  }
);

export const UpsellModal = forwardRef((props, ref) => {
  const {toggleUpsellModal, aboveOtherModals} = props;
  const isMediumAndUp = useIsMediumAndUp();
  const upsellModalRef = useRef(null);

  useEffect(() => {
    if (!upsellModalRef.current) return;

    upsellModalRef.current.style.height = `${upsellModalRef.current.offsetHeight}px`;
  }, []);

  // TODO Refactor to use ResponsiveModal
  return createPortal(
    <div
      className={cx('create-flow__upsell-modal', {
        'create-flow__upsell-modal--above-others': aboveOtherModals,
      })}
    >
      {/* TODO: Refactor to PB Button component */}
      <button
        className="unstyled-button create-flow__upsell-modal-overlay"
        onClick={toggleUpsellModal}
        type="button"
        aria-label="Upsell modal backdrop"
      />
      {isMediumAndUp ? (
        <UpsellModalContent {...props} ref={ref} />
      ) : (
        <BottomSheet
          content={<UpsellModalContent {...props} ref={ref} />}
          onClose={() => toggleUpsellModal(false)}
          aboveOtherModals
          in
        />
      )}
    </div>,
    document.body
  );
});
